import React, { useEffect } from "react";
import { ArrowLeft, ArrowRight, BookOpen, Globe, Music, Mail, ExternalLink, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Link } from "react-router-dom";

const steps = [
  {
    number: "01",
    title: "LOCATE YOUR IPI/CAE NUMBER",
    description: "Your Interested Parties Information (IPI) number is your unique 9-digit identifier assigned by your PRO (BMI, ASCAP, SESAC). We need this to register your splits.",
    action: "LOGIN TO BMI / ASCAP",
    icon: Globe
  },
  {
    number: "02",
    title: "REGISTER WITH SOUNDEXCHANGE",
    description: "PROs only collect performance royalties. SoundExchange collects digital performance royalties (SiriusXM, Pandora). You must have an account to collect your artist share.",
    action: "GO TO SOUNDEXCHANGE",
    icon: Music
  },
  {
    number: "03",
    title: "LINK ITZGREEN AS ADMINISTRATOR",
    description: "Once we have your IPI, we will add you to our publisher account as a 'Controlled Writer' for specific assets. You maintain 100% of your Writer's Share.",
    action: "EMAIL IPI NUMBER",
    icon: Mail,
    isPrimary: true
  }
];

const RoyaltySetupPage = () => {
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden" data-testid="royalty-setup-page">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10 sm:mb-12"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={14} />
          BACK TO PORTAL
        </Link>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12 sm:mb-16"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] sm:text-xs tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <BookOpen size={12} className="mr-2" />
            ADMINISTRATION GUIDE
          </Badge>

          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            ROYALTY <span className="text-[rgb(218,255,1)]">SETUP</span>
          </h1>
          <p
            className="text-[rgb(161,161,170)] text-sm sm:text-base max-w-lg mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Complete these steps to ensure you receive 100% of your performance and mechanical royalties.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-6">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                className={`bg-[rgb(26,28,30)] border ${step.isPrimary ? 'border-[rgb(218,255,1)]' : 'border-[rgba(255,255,255,0.08)]'} rounded-xl p-6 sm:p-8 relative overflow-hidden`}
              >
                {step.isPrimary && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-[rgb(218,255,1)]" />
                )}
                
                <div className="flex items-start gap-5">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${step.isPrimary ? 'bg-[rgb(218,255,1)] text-[rgb(17,17,19)]' : 'bg-[rgba(218,255,1,0.1)] text-[rgb(218,255,1)]'}`}>
                    <Icon size={24} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[rgb(161,161,170)] text-xs font-mono">
                        {step.number}
                      </span>
                      <h3 className="text-white text-lg font-bold tracking-wide" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {step.title}
                      </h3>
                    </div>
                    
                    <p className="text-[rgb(161,161,170)] text-sm leading-relaxed mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {step.description}
                    </p>
                    
                    {step.isPrimary ? (
                      <a
                        href="mailto:leaver@itzgreenmusic.com?subject=IPI%20NUMBER%20-%20Royalty%20Setup"
                        className="inline-flex items-center gap-2 bg-[rgb(218,255,1)] text-[rgb(17,17,19)] text-xs font-semibold tracking-[0.1em] px-6 py-3 rounded-lg hover:bg-[rgb(166,190,21)] transition-colors"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {step.action}
                        <ArrowRight size={14} />
                      </a>
                    ) : (
                      <button className="inline-flex items-center gap-2 text-[rgb(218,255,1)] text-xs font-semibold tracking-[0.1em] hover:text-white transition-colors">
                        {step.action}
                        <ExternalLink size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <Separator className="bg-[rgb(63,63,63)] my-12" />

        {/* Info Box */}
        <div className="bg-[rgba(74,222,128,0.05)] border border-[rgba(74,222,128,0.2)] rounded-xl p-6 flex items-start gap-4">
          <CheckCircle size={24} className="text-[rgb(74,222,128)] flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-white text-sm font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
              ALREADY REGISTERED?
            </h4>
            <p className="text-[rgb(161,161,170)] text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              If you have already submitted your IPI number via the Ingestion Portal or email, no further action is required. We will confirm your registration status within 48 hours.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default RoyaltySetupPage;
